import { PushNotifications } from '@capacitor/push-notifications';
import type { PushNotificationSchema, ActionPerformed } from '@capacitor/push-notifications';
import { isNativeApp } from './api';
import api from './api';
import { useNotificationsStore } from '../stores/notifications';
import { resolveNotificationLink } from '../utils/notificationLinks';
import { calendarEventPath } from '../utils/calendarLink';
import {
  initWebPushNotifications,
  deregisterWebPushNotifications,
} from './firebase-messaging';

// Deep link map: notification type → route path
const NOTIFICATION_ROUTES: Record<string, (data: Record<string, string>) => string> = {
  ticket_reply: (d) => `/tickets/${d.entityId}`,
  ticket_status_changed: (d) => `/tickets/${d.entityId}`,
  ticket_created: (d) => `/tickets/${d.entityId}`,
  ticket_assigned: (d) => `/tickets/${d.entityId}`,
  task_assigned: (d) => `/tasks/${d.entityId}`,
  task_created: (d) => `/tasks/${d.entityId}`,
  task_completed: (d) => `/tasks/${d.entityId}`,
  task_comment: (d) => `/tasks/${d.entityId}`,
  task_status_changed: (d) => `/tasks/${d.entityId}`,
  message_received: (d) => (d.entityId ? `/messages/${d.entityId}` : '/messages'),
  message_mentioned: (d) => (d.entityId ? `/messages/${d.entityId}` : '/messages'),
  project_assigned: (d) => `/projects/${d.entityId}`,
  project_created: (d) => `/projects/${d.entityId}`,
  project_status_changed: (d) => `/projects/${d.entityId}`,
  leave_request: () => '/leaves',
  leave_approved: () => '/leaves',
  leave_rejected: () => '/leaves',
  event_invitation: (d) => calendarEventPath(d.eventId),
  event_updated: (d) => calendarEventPath(d.eventId),
  event_cancelled: (d) => calendarEventPath(d.eventId),
  event_reminder: (d) => calendarEventPath(d.eventId),
  appreciation: () => '/dashboard',
  announcement: () => '/dashboard',
  notice_created: () => '/notices',
  asset_assigned: () => '/assets',
  tdrive_file_shared: (d) => (d.fileId ? `/tdrive?tab=shared-with-me&file=${d.fileId}` : '/tdrive?tab=shared-with-me'),
  tdrive_folder_shared: (d) => (d.folderId ? `/tdrive?tab=shared-with-me&folder=${d.folderId}` : '/tdrive?tab=shared-with-me'),
  document_shared: () => '/tdrive',
};

let routerNavigate: ((path: string) => void) | null = null;

/**
 * Set the navigation function (called once from App.vue with the router instance)
 */
export function setPushNavigator(navigate: (path: string) => void): void {
  routerNavigate = navigate;
}

function navigateToNotification(data: Record<string, string>): void {
  if (!routerNavigate || !data.type) return;

  // Attendance review / W-2 types: the (validated) url / entityUrl of the payload.
  const link = resolveNotificationLink(data.type, data);
  if (link) {
    routerNavigate(link);
    return;
  }

  const routeFn = NOTIFICATION_ROUTES[data.type];
  if (routeFn) {
    const path = routeFn(data);
    routerNavigate(path);
  } else {
    routerNavigate('/notifications');
  }
}

/**
 * Register device with FCM and save token to backend.
 * Call this after the user successfully logs in.
 *
 * Two branches:
 *  - Capacitor native (iOS / Android): uses @capacitor/push-notifications
 *  - PWA / browser: uses Firebase Web Messaging SDK via firebase-messaging.ts
 */
export async function initPushNotifications(): Promise<void> {
  if (!isNativeApp()) {
    // Browser / PWA path
    await initWebPushNotifications();
    return;
  }

  try {
    const permResult = await PushNotifications.requestPermissions();
    if (permResult.receive !== 'granted') return;

    await PushNotifications.register();

    await PushNotifications.addListener('registration', async (token: { value: string }) => {
      try {
        const platform = (window as any).Capacitor?.getPlatform?.() === 'ios' ? 'ios' : 'android';
        await api.post('/auth/fcm-token', { token: token.value, platform });
      } catch {
        // Non-critical
      }
    });

    await PushNotifications.addListener('registrationError', () => {
      // Push notifications unavailable on this device
    });

    await PushNotifications.addListener(
      'pushNotificationReceived',
      (notification: PushNotificationSchema) => {
        // App is in foreground — OS suppresses native banner, show in-app toast instead
        try {
          const store = useNotificationsStore();
          store.addNotification({
            id: Date.now(),
            type: (notification.data as any)?.type || 'general',
            title: notification.title || '',
            message: notification.body || '',
            data: notification.data as Record<string, unknown>,
            readAt: null,
            createdAt: new Date().toISOString(),
          });
        } catch {
          // Non-critical
        }
      }
    );

    await PushNotifications.addListener(
      'pushNotificationActionPerformed',
      (action: ActionPerformed) => {
        const data = action.notification.data as Record<string, string>;
        navigateToNotification(data);
      }
    );
  } catch {
    // Non-critical — app works without push notifications
  }
}

/**
 * Clean up on logout — prevents push to logged-out devices.
 */
export async function deregisterPushNotifications(): Promise<void> {
  if (!isNativeApp()) {
    await deregisterWebPushNotifications();
    return;
  }

  try {
    await PushNotifications.removeAllDeliveredNotifications();
    await PushNotifications.removeAllListeners();
  } catch {
    // Non-critical
  }
}
